import { GoogleGenerativeAI } from '@google/generative-ai';

// Initialize Gemini API - browser-safe
const geminiKey = import.meta.env.VITE_GEMINI_API_KEY;
const genAI = geminiKey ? new GoogleGenerativeAI(geminiKey) : null;

// Check if we're in development mode for logging
const isDev = import.meta.env.DEV;

export const isGeminiConfigured = () => !!genAI && !!geminiKey;

// Test function to validate API key and model availability
export const testGeminiConnection = async () => {
  if (!genAI || !geminiKey) {
    return { success: false, error: 'API key not configured' };
  }

  try {
    if (isDev) console.log('Testing Gemini API connection...');
    const model = genAI.getGenerativeModel({ model: 'gemini-1.5-pro' });

    const testPrompt = 'Say "Hello" in one word.';
    const result = await model.generateContent(testPrompt);

    const response = await result.response;
    const text = response.text();

    if (isDev) console.log('Gemini API test successful:', text);
    return { success: true, response: text };
  } catch (error) {
    if (isDev) console.error('Gemini API test failed:', error);
    return { success: false, error: error.message };
  }
};

export const generateDesignHint = async ({ challenge, objects = [], simpleMode = false }) => {
  // Handle no objects in scene - return local hint without API call
  if (!objects || objects.length === 0) {
    if (isDev) console.log('No objects in scene - returning local hint');
    return {
      hint: 'Start by adding some objects to your scene. Try placing a cube or cylinder to begin your design.',
      success: true,
      local: true
    };
  }

  // Handle undefined challenge - safe fallback message
  if (!challenge) {
    if (isDev) console.log('Challenge is undefined - returning safe fallback');
    return {
      hint: 'Try checking object size, rotation, and placement.',
      success: true,
      local: true
    };
  }

  // Check API configuration
  if (!genAI || !geminiKey) {
    if (isDev) console.error('Gemini API not configured: missing API key');
    return {
      hint: 'Please set up your Gemini API key in the .env file (VITE_GEMINI_API_KEY) to use AI hints.',
      success: false,
      error: 'No API key configured'
    };
  }

  try {
    // Use EXACTLY gemini-1.5-pro model as specified
    const modelName = 'gemini-1.5-pro';
    if (isDev) console.log(`Using Gemini model: ${modelName}`);

    const model = genAI.getGenerativeModel({ model: modelName });

    const prompt = buildHintPrompt({ challenge, objects, simpleMode });
    if (isDev) console.log('Generated prompt:', prompt.substring(0, 100) + '...');

    if (isDev) console.log('Making Gemini API call...');

    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout after 30 seconds')), 30000);
    });

    const apiPromise = model.generateContent(prompt);
    const result = await Promise.race([apiPromise, timeoutPromise]);

    const response = await result.response;
    const text = response.text();

    if (isDev) console.log('Gemini API call successful, response length:', text.length);

    return {
      hint: text,
      success: true
    };
  } catch (error) {
    if (isDev) {
      console.error('Gemini API error in generateDesignHint:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
    }

    // Provide readable UI error messages
    let errorMessage = 'AI mentor unavailable. Try again.';
    if (error.message?.includes('API_KEY') || error.message?.includes('INVALID_ARGUMENT')) {
      errorMessage = 'API key configuration issue. Please check your setup.';
    } else if (error.message?.includes('QUOTA') || error.message?.includes('RATE_LIMIT')) {
      errorMessage = 'API quota exceeded. Please try again later.';
    } else if (error.message?.includes('NETWORK') || error.message?.includes('fetch') || error.message?.includes('Failed to fetch')) {
      errorMessage = 'Network connection issue. Please check your internet.';
    } else if (error.message?.includes('timeout')) {
      errorMessage = 'Request timed out. Please try again.';
    } else if (error.message?.includes('model') && error.message?.includes('not found')) {
      errorMessage = 'AI model not available. Please try again later.';
    }

    return {
      hint: errorMessage,
      success: false,
      error: error.message
    };
  }
};

const buildHintPrompt = ({ challenge, objects, simpleMode }) => {
  // Safe property access - never destructure undefined challenge
  const title = challenge?.title || 'Unknown Challenge';
  const description = challenge?.description || 'No description available';
  const objectives = Array.isArray(challenge?.objectives) ? challenge.objectives : [];
  const assistiveTechFocus = challenge?.assistiveTechFocus || false;
  const realWorldContext = challenge?.realWorldContext || '';

  let prompt = `You are an AI Design Mentor helping someone learn CAD design for assistive technology.

CHALLENGE: ${title}
DESCRIPTION: ${description}
${assistiveTechFocus ? 'FOCUS: This is an assistive technology design challenge' : ''}

CURRENT SCENE: ${buildSceneSummary(objects)}

OBJECTIVES: ${(objectives.length > 0) ? objectives.join(', ') : 'Complete the design task'}

${realWorldContext ? `REAL-WORLD CONTEXT: ${realWorldContext}` : ''}

${simpleMode ? 'EXPLAIN LIKE IM 12: Use very simple language, avoid technical jargon.' : 'Use appropriate technical terms but explain them.'}

TASK: Provide a contextual hint about what the user should do next. DO NOT give the answer directly. Instead:
1. Explain what they're missing based on the scene state
2. Why that step matters in real-world design
3. Give a gentle nudge toward the solution
4. Keep it encouraging and educational

RESPONSE FORMAT: Keep it concise (2-4 sentences). Focus on design thinking, not just clicking buttons.`;

  return prompt;
};

const buildSceneSummary = (objects) => {
  if (!objects || !Array.isArray(objects) || objects.length === 0) {
    return 'No objects in scene yet';
  }

  let summary = '';

  objects.forEach((obj, index) => {
    if (!obj) {
      if (isDev) console.warn(`Object at index ${index} is undefined or null`);
      return;
    }

    const objType = obj.type || 'unknown';
    const position = obj.position && Array.isArray(obj.position) ? obj.position.join(', ') : 'unknown';

    summary += `Object ${index + 1}: ${objType} at position (${position})`;

    // Safe rotation handling
    if (obj.rotation && Array.isArray(obj.rotation)) {
      try {
        const rotationDegrees = obj.rotation.map(r => (r * 180 / Math.PI).toFixed(1));
        summary += `, rotation (${rotationDegrees.join('°, ')}°)`;
      } catch (error) {
        if (isDev) console.warn('Error processing rotation for object', index, error);
      }
    }

    // Safe scale handling
    if (obj.scale && Array.isArray(obj.scale)) {
      summary += `, scale (${obj.scale.join(', ')})`;
    }

    // Safe material/color handling
    if (obj.material && obj.material.color) {
      summary += `, color: ${obj.material.color}`;
    }

    summary += '\n';
  });

  return summary.trim();
};

export const generateRealWorldExplanation = async (challengeData, completedAction) => {
  if (!genAI || !geminiKey) {
    if (isDev) console.error('Gemini API not configured for generateRealWorldExplanation');
    return {
      explanation: 'This design choice improves accessibility and user experience.',
      success: false,
      error: 'No API key configured'
    };
  }

  if (!challengeData) {
    if (isDev) console.error('generateRealWorldExplanation called with undefined challengeData');
    return {
      explanation: 'This design choice improves accessibility and user experience.',
      success: false,
      error: 'No challenge data'
    };
  }

  try {
    const modelName = 'gemini-1.5-pro';
    if (isDev) console.log(`Using Gemini model for explanation: ${modelName}`);

    const model = genAI.getGenerativeModel({ model: modelName });

    // Safe property access
    const title = challengeData.title || 'Unknown';
    const realWorldContext = challengeData.realWorldContext || 'General assistive technology design';

    const prompt = `You are explaining why a specific design action matters in real-world assistive technology.

CHALLENGE: ${title}
ACTION JUST COMPLETED: ${completedAction || 'Unknown action'}
REAL-WORLD CONTEXT: ${realWorldContext}

Explain in 1-2 sentences why this design choice is important for accessibility and user experience. Make it practical and inspiring.`;

    if (isDev) console.log('Making Gemini API call for explanation...');

    // Add timeout to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timeout after 30 seconds')), 30000);
    });

    const apiPromise = model.generateContent(prompt);
    const result = await Promise.race([apiPromise, timeoutPromise]);

    const response = await result.response;
    const text = response.text();

    if (isDev) console.log('Gemini explanation API call successful, response length:', text.length);

    return {
      explanation: text,
      success: true
    };
  } catch (error) {
    if (isDev) {
      console.error('Gemini API error in generateRealWorldExplanation:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
    }

    // Provide readable UI error messages
    let errorMessage = 'This design choice improves accessibility and user experience.';
    if (error.message?.includes('API_KEY')) {
      errorMessage = 'API key configuration issue.';
    } else if (error.message?.includes('QUOTA') || error.message?.includes('RATE_LIMIT')) {
      errorMessage = 'API quota exceeded.';
    }

    return {
      explanation: errorMessage,
      success: false,
      error: error.message
    };
  }
};